import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import * as Icons from 'lucide-react';
import { CONFIG_TEMPLATES_DATA, DMS_ALARMS_DATA, ADAS_ALARMS_DATA } from './config/deviceConfigData';

const ICON_MAP = new Proxy(Icons, {
  get(target, prop) {
    return target[prop] || Icons.HelpCircle;
  }
});

const {
  ToggleRight,
  ToggleLeft,
  Eye,
  User,
  Car,
  Gauge,
  Volume2,
  Shield,
  Power,
  Sun,
  Moon,
  ArrowLeftRight,
  Video,
  Camera,
  Server,
  Plus,
  Trash2,
  Lock,
  Zap,
  FileJson,
  CheckCircle
} = ICON_MAP;

const CONFIG_TEMPLATES = Object.fromEntries(
  Object.entries(CONFIG_TEMPLATES_DATA).map(([key, template]) => [
    key,
    {
      ...template,
      icon: ICON_MAP[template.iconKey]
    }
  ])
);

const DMS_ALARMS = DMS_ALARMS_DATA.map(alarm => ({
  ...alarm,
  icon: ICON_MAP[alarm.iconKey]
}));

const ADAS_ALARMS = ADAS_ALARMS_DATA.map(alarm => ({
  ...alarm,
  icon: ICON_MAP[alarm.iconKey]
}));

const ALL_ALARMS = [...DMS_ALARMS, ...ADAS_ALARMS];

function DeviceConfigPanel({ onConfigGenerated }) {
  const [selectedConfig, setSelectedConfig] = useState('enableAiEvents');
  
  // AI Events state
  const [aiEvents, setAiEvents] = useState({
    0: true, 1: true, 2: true, 3: true, 4: true, 5: false,
    6: true, 7: true, 9: true, 10: true, 11: true, 16: true, 22: true, 31: true
  });

  // CAN/Speed settings
  const [canBusRate, setCanBusRate] = useState(1);
  const [canPluginType, setCanPluginType] = useState(1);
  const [speedSource, setSpeedSource] = useState(5);

  // Sound volume
  const [soundVolume, setSoundVolume] = useState(50);

  // Privacy mode
  const [privacyEnabled, setPrivacyEnabled] = useState(true);
  const [privacyAudioSuppression, setPrivacyAudioSuppression] = useState(0);
  const PRIVACY_EVENT_MODE = 1;
  const [privacyVideoOutput, setPrivacyVideoOutput] = useState(1);
  const [privacyEventSensitivity, setPrivacyEventSensitivity] = useState(5);
  const [privacyChannels, setPrivacyChannels] = useState([2]); // Array of selected channels
  const [privacyTriggerMode, setPrivacyTriggerMode] = useState(1);

  // Remote wakeup
  const [remoteWakeEnabled, setRemoteWakeEnabled] = useState(true);
  const [sleepMode, setSleepMode] = useState(1);
  const [lowPowerTimeout, setLowPowerTimeout] = useState(100);

  // Driver assist - Turn signals
  const [leftSignalEnabled, setLeftSignalEnabled] = useState(true);
  const [rightSignalEnabled, setRightSignalEnabled] = useState(true);
  const [rearSignalEnabled, setRearSignalEnabled] = useState(true);
  const [lightOffMode, setLightOffMode] = useState(1);
  const [lightOffTime, setLightOffTime] = useState(1);
  // Trigger Option for sensors: 0=Source Voltage, 1=Source Pulse, 2=CAN BUS
  const [triggerOption, setTriggerOption] = useState(2);

  // Video settings
  const [mainChannelEnabled, setMainChannelEnabled] = useState([true, false, false, false]);
  const MAIN_RESOLUTION_DEFAULT = [9, 6, 6, 6];
  const [mainFrameRate, setMainFrameRate] = useState([24, 20, 20, 20]);
  const [mainQuality, setMainQuality] = useState([1, 1, 1, 1]);

  // Server settings - dynamic array of servers
  const [servers, setServers] = useState([
    { address: 'ft-api.prod.trimblevi.com', port: 21083, enabled: true },
    { address: '18.231.35.6', port: 5556, enabled: true }
  ]);

  // User password
  const [adminPassword, setAdminPassword] = useState('');
  const [userPassword, setUserPassword] = useState('');

  // M1N Large Model
  const [m1nLargeModelEnabled, setM1nLargeModelEnabled] = useState(true);

  // Custom JSON
  const [customJsonText, setCustomJsonText] = useState(JSON.stringify({ MDVR: {} }, null, 2));
  const [customJsonObject, setCustomJsonObject] = useState({ MDVR: {} });
  const [customJsonError, setCustomJsonError] = useState(null);

  // Toggle all AI events
  const toggleAllAiEvents = (enabled) => {
    const newState = {};
    ALL_ALARMS.forEach(a => newState[a.index] = enabled);
    setAiEvents(newState);
  };

  // Toggle all DMS events
  const toggleAllDmsEvents = (enabled) => {
    setAiEvents(prev => {
      const newState = { ...prev };
      DMS_ALARMS.forEach(a => newState[a.index] = enabled);
      return newState;
    });
  };

  // Toggle all ADAS events
  const toggleAllAdasEvents = (enabled) => {
    setAiEvents(prev => {
      const newState = { ...prev };
      ADAS_ALARMS.forEach(a => newState[a.index] = enabled);
      return newState;
    });
  };

  // Build configuration JSON based on selected type
  const buildConfig = () => {
    switch (selectedConfig) {
      case 'enableAiEvents':
        return buildAiEventsConfig();
      case 'speedSettings':
        return buildSpeedConfig();
      case 'soundVolume':
        return buildSoundConfig();
      case 'privacyMode':
        return buildPrivacyConfig();
      case 'remoteWakeup':
        return buildRemoteWakeupConfig();
      case 'driverAssist':
        return buildDriverAssistConfig();
      case 'videoSettings':
        return buildVideoConfig();
      case 'serverSettings':
        return buildServerConfig();
      case 'userPassword':
        return buildUserPasswordConfig();
      case 'm1nLargeModel':
        return buildM1nLargeModelConfig();
      case 'customJson':
        return customJsonObject;
      default:
        return { MDVR: {} };
    }
  };

  const buildAiEventsConfig = () => {
    const dsmaArray = [];
    for (let i = 0; i <= 31; i++) {
      if (aiEvents.hasOwnProperty(i)) {
        dsmaArray.push({ EN: aiEvents[i] ? 1 : 0 });
      } else {
        dsmaArray.push(null);
      }
    }
    return { MDVR: { DSM: { DSMA: dsmaArray } } };
  };

  const buildSpeedConfig = () => ({
    MDVR: {
      CAN: { CA: [{ BR: canBusRate, FT: canPluginType }, {}, {}, {}] },
      SAP: { SF: speedSource }
    }
  });

  const buildSoundConfig = () => ({
    MDVR: { GSP: { TVOL: String(soundVolume) } }
  });

  const buildPrivacyConfig = () => {
    // Convert selected channels array to bitmask (channel 1 = bit 0, channel 2 = bit 1, etc.)
    const channelBitmask = privacyChannels.reduce((mask, ch) => mask | (1 << (ch - 1)), 0);
    return {
      MDVR: {
        PRVAP: {
          AS: privacyAudioSuppression,
          EN: privacyEnabled ? 1 : 0,
          EM: PRIVACY_EVENT_MODE,
          ENVO: privacyVideoOutput,
          ES: privacyEventSensitivity,
          PRCH: channelBitmask,
          TM: privacyTriggerMode
        }
      }
    };
  };

  const buildRemoteWakeupConfig = () => ({
    MDVR: {
      SSP: { UPT: 0 },
      TSP: { PAVM: 0 },
      VPP: { SBM: sleepMode },
      SWUS: { RTWP: remoteWakeEnabled ? 1 : 0 },
      LPDOWN: { TM: lowPowerTimeout }
    }
  });

  const buildDriverAssistConfig = () => ({
    MDVR: {
      SSP: { CPM: lightOffMode, UTIME: String(lightOffTime) },
      CAN: { CA: [{ BR: canBusRate, FT: canPluginType }, {}, {}, {}] },
      IOP: [
        {
          EN: leftSignalEnabled ? 1 : 0, N: "Left", S: "S1",
          AS: 0, UT: 20, TM: triggerOption, SDT: 2,
          APR: { SM: 1, ST: 2, CH: [3, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255] }
        },
        {
          EN: rightSignalEnabled ? 1 : 0, N: "Right", S: "S2",
          AS: 0, UT: 19, TM: triggerOption, SDT: 2,
          APR: { SM: 1, ST: 2, CH: [1, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255] }
        },
        {
          EN: rearSignalEnabled ? 1 : 0, N: "Rear", S: "S3",
          AS: 0, UT: 24, TM: triggerOption, SDT: 2,
          APR: { SM: 1, ST: 2, CH: [2, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255] }
        },
        {}, {}
      ]
    }
  });

  const buildVideoConfig = () => ({
    MDVR: {
      EOSD: [
        { DE: 1, VE: 0, TE: 1, SE: 0, GE: 0 },
        { DE: 1, VE: 0, TE: 1, SE: 0, GE: 0 },
        { DE: 1, VE: 0, TE: 1, SE: 0, GE: 0 },
        { DE: 1, VE: 0, TE: 1, SE: 0, GE: 0 }
      ],
      MAIN: mainChannelEnabled.map((enabled, i) => ({
        QLT: mainQuality[i], FR: mainFrameRate[i], ECT: 1,
        RST: MAIN_RESOLUTION_DEFAULT[i], VEN: enabled ? 1 : 0, ...(i === 0 ? { AIEN: 1 } : {})
      })),
      AR: {
        VEC: mainChannelEnabled.map((enabled, i) => ({
          QLT: 1, FR: 15, ECT: 1, RST: 2, VEN: i === 0 ? 1 : 0
        }))
      }
    }
  });


  const buildServerConfig = () => ({
    MDVR: {
      MCMS: {
        M: 15,
        SP: servers.map(server => ({
          EN: server.enabled ? 1 : 0,
          CPORT: server.port,
          CTPORT: 6556,
          CS: server.address,
          ETLS: 0,
          MPORT: server.port,
          MTPORT: 6556,
          MS: server.address,
          NWT: 2
        }))
      }
    }
  });

  const buildUserPasswordConfig = () => ({
    MDVR: {
      USRMNG: {
        USRINFO: [
          { USED: 1, USRNAME: "admin", USRPWD: adminPassword || "admin" },
          { USED: 1, USRNAME: "user", USRPWD: userPassword || "user" },
          { USRNAME: "", USED: 0, USRPWD: "" }
        ]
      }
    }
  });


  const buildM1nLargeModelConfig = () => ({
    MDVR: {
      IAP: {
        SUP: [
          null,
          {
            PARA: {
              STANDARDPARAINFO: {
                ADAS: {
                  SPECIALFUNCTION: [
                    null, null, null, null, null, null, null,
                    m1nLargeModelEnabled ? 1 : 0
                  ]
                }
              }
            }
          }
        ],
        LRR: 0
      }
    }
  });

  // Render configuration form based on selected type
  const renderConfigForm = () => {
    const template = CONFIG_TEMPLATES[selectedConfig];
    if (!template) return null;

    switch (selectedConfig) {
      case 'enableAiEvents':
        return renderAiEventsForm();
      case 'speedSettings':
        return renderSpeedForm();
      case 'soundVolume':
        return renderSoundForm();
      case 'privacyMode':
        return renderPrivacyForm();
      case 'remoteWakeup':
        return renderRemoteWakeupForm();
      case 'driverAssist':
        return renderDriverAssistForm();
      case 'videoSettings':
        return renderVideoForm();
      case 'serverSettings':
        return renderServerForm();
      case 'userPassword':
        return renderUserPasswordForm();
      case 'm1nLargeModel':
        return renderM1nLargeModelForm();
      case 'customJson':
        return renderCustomJsonForm();
      default:
        return <p className="text-muted-foreground">Configuration not implemented yet</p>;
    }
  };

  const renderAlarmGrid = (alarms) => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
      {alarms.map((alarm) => {
        const Icon = alarm.icon;
        const isEnabled = aiEvents[alarm.index];
        return (
          <button
            key={alarm.index}
            onClick={() => setAiEvents(prev => ({ ...prev, [alarm.index]: !prev[alarm.index] }))}
            className={`p-3 rounded-lg border-2 transition-all ${
              isEnabled
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 bg-gray-50 opacity-60'
            }`}
            title={alarm.description}
          >
            <div className="flex items-center justify-between mb-1">
              <Icon className={`w-5 h-5 ${isEnabled ? 'text-green-600' : 'text-gray-400'}`} />
              {isEnabled ? (
                <ToggleRight className="w-5 h-5 text-green-600" />
              ) : (
                <ToggleLeft className="w-5 h-5 text-gray-400" />
              )}
            </div>
            <span className={`text-xs font-medium ${isEnabled ? 'text-green-700' : 'text-gray-500'}`}>
              {alarm.label}
            </span>
          </button>
        );
      })}
    </div>
  );

  const renderAiEventsForm = () => (
    <div className="space-y-6">
      {/* Global Controls */}
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-purple-800 flex items-center gap-2">
          <Eye className="w-5 h-5" />
          AI Detection Events
        </h4>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => toggleAllAiEvents(true)}>
            Enable All
          </Button>
          <Button variant="outline" size="sm" onClick={() => toggleAllAiEvents(false)}>
            Disable All
          </Button>
        </div>
      </div>

      {/* DMS Events */}
      <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center justify-between">
          <h5 className="font-medium text-blue-800 flex items-center gap-2">
            <User className="w-4 h-4" />
            DMS (Driver Monitoring System)
          </h5>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => toggleAllDmsEvents(true)}>
              All On
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => toggleAllDmsEvents(false)}>
              All Off
            </Button>
          </div>
        </div>
        {renderAlarmGrid(DMS_ALARMS)}
      </div>

      {/* ADAS Events */}
      <div className="space-y-3 p-4 bg-cyan-50 rounded-lg border border-cyan-200">
        <div className="flex items-center justify-between">
          <h5 className="font-medium text-cyan-800 flex items-center gap-2">
            <Car className="w-4 h-4" />
            ADAS (Advanced Driver Assistance)
          </h5>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => toggleAllAdasEvents(true)}>
              All On
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => toggleAllAdasEvents(false)}>
              All Off
            </Button>
          </div>
        </div>
        {renderAlarmGrid(ADAS_ALARMS)}
      </div>
    </div>
  );

  const renderSpeedForm = () => (
    <div className="space-y-4">
      <h4 className="font-medium text-cyan-800 flex items-center gap-2">
        <Gauge className="w-5 h-5" />
        Speed & CAN Bus Settings
      </h4>
      
      <div className="space-y-2">
        <Label>CAN Bus Rate</Label>
        <div className="grid grid-cols-5 gap-2">
          {[
            { value: 0, label: '1000k' },
            { value: 1, label: '500k' },
            { value: 2, label: '250k' },
            { value: 3, label: '125k' },
            { value: 4, label: '100k' },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => setCanBusRate(opt.value)}
              className={`p-2 rounded-lg border-2 text-sm ${
                canBusRate === opt.value
                  ? 'border-cyan-500 bg-cyan-100 text-cyan-700'
                  : 'border-gray-200 hover:border-cyan-300'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Plugin Type</Label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: 0, label: 'NULL', desc: 'No plugin' },
            { value: 1, label: 'Generic', desc: 'J1939' },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => setCanPluginType(opt.value)}
              className={`p-3 rounded-lg border-2 text-left ${
                canPluginType === opt.value
                  ? 'border-cyan-500 bg-cyan-100'
                  : 'border-gray-200 hover:border-cyan-300'
              }`}
            >
              <span className="font-medium">{opt.label}</span>
              <span className="block text-xs text-gray-500">{opt.desc}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Speed Source</Label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: 0, label: 'Satellite', desc: 'GPS speed' },
            { value: 5, label: 'CAN Bus', desc: 'Vehicle speed' },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => setSpeedSource(opt.value)}
              className={`p-3 rounded-lg border-2 text-left ${
                speedSource === opt.value
                  ? 'border-cyan-500 bg-cyan-100'
                  : 'border-gray-200 hover:border-cyan-300'
              }`}
            >
              <span className="font-medium">{opt.label}</span>
              <span className="block text-xs text-gray-500">{opt.desc}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSoundForm = () => (
    <div className="space-y-4">
      <h4 className="font-medium text-orange-800 flex items-center gap-2">
        <Volume2 className="w-5 h-5" />
        Sound Volume
      </h4>
      
      <div className="space-y-2">
        <Label>Volume Level: {soundVolume}%</Label>
        <input
          type="range"
          min="0"
          max="100"
          value={soundVolume}
          onChange={(e) => setSoundVolume(parseInt(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>Mute</span>
          <span>Max</span>
        </div>
      </div>
    </div>
  );

  const renderPrivacyForm = () => (
    <div className="space-y-4">
      <h4 className="font-medium text-red-800 flex items-center gap-2">
        <Shield className="w-5 h-5" />
        Privacy Mode Settings
      </h4>
      
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <Label>Privacy Mode Enabled</Label>
        <button
          onClick={() => setPrivacyEnabled(!privacyEnabled)}
          className={`p-2 rounded ${privacyEnabled ? 'bg-green-500 text-white' : 'bg-gray-300'}`}
        >
          {privacyEnabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Alarm Type</Label>
          <select
            value={privacyAudioSuppression}
            onChange={(e) => setPrivacyAudioSuppression(parseInt(e.target.value))}
            className="w-full p-2 border rounded"
          >
            <option value={0}>Event</option>
            <option value={1}>Alarm</option>
          </select>
        </div>

        <div className="space-y-2">
          <Label>Video Output</Label>
          <select
            value={privacyVideoOutput}
            onChange={(e) => setPrivacyVideoOutput(parseInt(e.target.value))}
            className="w-full p-2 border rounded"
          >
            <option value={0}>Disabled</option>
            <option value={1}>Enabled</option>
          </select>
        </div>

        <div className="space-y-2">
          <Label>Privacy Method</Label>
          <select
            value={privacyTriggerMode}
            onChange={(e) => setPrivacyTriggerMode(parseInt(e.target.value))}
            className="w-full p-2 border rounded"
          >
            <option value={1}>IO</option>
            <option value={2}>ACC OFF</option>
          </select>
        </div>

        <div className="space-y-2">
          <Label>Speed (mph)</Label>
          <Input
            type="number"
            min={0}
            max={200}
            value={privacyEventSensitivity}
            onChange={(e) => setPrivacyEventSensitivity(parseInt(e.target.value))}
          />
        </div>

        <div className="space-y-2">
          <Label>Channels (select multiple)</Label>
          <div className="grid grid-cols-4 gap-2">
            {[1, 2, 3, 4].map(ch => {
              const isSelected = privacyChannels.includes(ch);
              return (
                <button
                  key={ch}
                  onClick={() => {
                    if (isSelected) {
                      setPrivacyChannels(prev => prev.filter(c => c !== ch));
                    } else {
                      setPrivacyChannels(prev => [...prev, ch].sort());
                    }
                  }}
                  className={`p-2 rounded-lg border-2 text-center ${
                    isSelected
                      ? 'border-red-500 bg-red-100 text-red-700'
                      : 'border-gray-200 bg-gray-50 text-gray-500'
                  }`}
                >
                  CH {ch}
                </button>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );

  const renderRemoteWakeupForm = () => (
    <div className="space-y-4">
      <h4 className="font-medium text-green-800 flex items-center gap-2">
        <Power className="w-5 h-5" />
        Remote Wakeup Settings
      </h4>
      
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <Label>Remote Wake Enabled</Label>
        <button
          onClick={() => setRemoteWakeEnabled(!remoteWakeEnabled)}
          className={`p-2 rounded ${remoteWakeEnabled ? 'bg-green-500 text-white' : 'bg-gray-300'}`}
        >
          {remoteWakeEnabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
        </button>
      </div>

      <div className="space-y-2">
        <Label>Sleep Mode</Label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: 0, label: 'No Consumption Standby', icon: Sun },
            { value: 1, label: 'Low Power Standby', icon: Moon },
          ].map((opt) => {
            const Icon = opt.icon;
            return (
              <button
                key={opt.value}
                onClick={() => setSleepMode(opt.value)}
                className={`p-3 rounded-lg border-2 flex items-center gap-2 ${
                  sleepMode === opt.value
                    ? 'border-green-500 bg-green-100'
                    : 'border-gray-200 hover:border-green-300'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Sleep Time (seconds)</Label>
        <Input
          type="number"
          min={1}
          max={3600}
          value={lowPowerTimeout}
          onChange={(e) => setLowPowerTimeout(parseInt(e.target.value))}
        />
      </div>
    </div>
  );

  const renderDriverAssistForm = () => (
    <div className="space-y-4">
      <h4 className="font-medium text-blue-800 flex items-center gap-2">
        <ArrowLeftRight className="w-5 h-5" />
        Driver Assist Settings
      </h4>
      
      <div className="space-y-2">
        <Label>Turn Signal Sensors</Label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'Left', enabled: leftSignalEnabled, setEnabled: setLeftSignalEnabled },
            { label: 'Right', enabled: rightSignalEnabled, setEnabled: setRightSignalEnabled },
            { label: 'Rear', enabled: rearSignalEnabled, setEnabled: setRearSignalEnabled },
          ].map((sensor) => (
            <button
              key={sensor.label}
              onClick={() => sensor.setEnabled(!sensor.enabled)}
              className={`p-3 rounded-lg border-2 ${
                sensor.enabled
                  ? 'border-blue-500 bg-blue-100'
                  : 'border-gray-200 bg-gray-50 opacity-60'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <Car className={`w-5 h-5 ${sensor.enabled ? 'text-blue-600' : 'text-gray-400'}`} />
                {sensor.enabled ? (
                  <ToggleRight className="w-5 h-5 text-blue-600" />
                ) : (
                  <ToggleLeft className="w-5 h-5 text-gray-400" />
                )}
              </div>
              <span className={`text-sm font-medium ${sensor.enabled ? 'text-blue-700' : 'text-gray-500'}`}>
                {sensor.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Light Off Mode</Label>
          <select
            value={lightOffMode}
            onChange={(e) => setLightOffMode(parseInt(e.target.value))}
            className="w-full p-2 border rounded"
          >
            <option value={0}>Never</option>
            <option value={1}>Custom</option>
          </select>
        </div>

        <div className="space-y-2">
          <Label>Light Off Time (sec)</Label>
          <Input
            type="number"
            min={1}
            max={3600}
            value={lightOffTime}
            onChange={(e) => setLightOffTime(parseInt(e.target.value))}
          />
        </div>
      </div>

      {/* Trigger Option */}
      <div className="space-y-2">
        <Label>Trigger Option (TM)</Label>
        <select
          value={triggerOption}
          onChange={(e) => setTriggerOption(parseInt(e.target.value))}
          className="w-full p-2 border rounded"
        >
          <option value={0}>Source Voltage</option>
          <option value={1}>Source Pulse</option>
          <option value={2}>CAN BUS</option>
        </select>
        <p className="text-xs text-muted-foreground">
          Applies to all turn signal sensors (Left, Right, Rear)
        </p>
      </div>
    </div>
  );

  const renderVideoForm = () => (
    <div className="space-y-4">
      <h4 className="font-medium text-pink-800 flex items-center gap-2">
        <Video className="w-5 h-5" />
        Video Settings
      </h4>
      
      <div className="space-y-2">
        <Label>Channel Recording</Label>
        <div className="grid grid-cols-4 gap-2">
          {[1, 2, 3, 4].map((ch, i) => (
            <button
              key={ch}
              onClick={() => {
                const newEnabled = [...mainChannelEnabled];
                newEnabled[i] = !newEnabled[i];
                setMainChannelEnabled(newEnabled);
              }}
              className={`p-3 rounded-lg border-2 ${
                mainChannelEnabled[i]
                  ? 'border-pink-500 bg-pink-100'
                  : 'border-gray-200 bg-gray-50 opacity-60'
              }`}
            >
              <Camera className={`w-5 h-5 mx-auto mb-1 ${mainChannelEnabled[i] ? 'text-pink-600' : 'text-gray-400'}`} />
              <span className="text-sm">CH {ch}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Frame Rate (FPS) per Channel</Label>
        <div className="grid grid-cols-4 gap-2">
          {[1, 2, 3, 4].map((ch, i) => (
            <div key={ch} className="space-y-1">
              <span className="text-xs text-gray-500">CH {ch}</span>
              <Input
                type="number"
                min={1}
                max={30}
                value={mainFrameRate[i]}
                onChange={(e) => {
                  const newFrameRate = [...mainFrameRate];
                  newFrameRate[i] = parseInt(e.target.value) || 1;
                  setMainFrameRate(newFrameRate);
                }}
                className="text-center"
              />
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500">Values: 1-30 FPS</p>
      </div>

      <div className="space-y-2">
        <Label>Quality (QLT) per Channel</Label>
        <div className="grid grid-cols-4 gap-2">
          {[1, 2, 3, 4].map((ch, i) => (
            <div key={ch} className="space-y-1">
              <span className="text-xs text-gray-500">CH {ch}</span>
              <select
                value={mainQuality[i]}
                onChange={(e) => {
                  const newQuality = [...mainQuality];
                  newQuality[i] = parseInt(e.target.value);
                  setMainQuality(newQuality);
                }}
                className="w-full p-2 border rounded text-center text-sm"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map(q => (
                  <option key={q} value={q}>{q} {q === 1 ? '(Best)' : q === 8 ? '(Lowest)' : ''}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500">1 = Best quality, 8 = Lowest quality</p>
      </div>
    </div>
  );


  const addServer = () => {
    setServers([...servers, { address: '', port: 5556, enabled: true }]);
  };

  const removeServer = (index) => {
    if (servers.length > 1) {
      setServers(servers.filter((_, i) => i !== index));
    }
  };

  const updateServer = (index, field, value) => {
    const newServers = [...servers];
    newServers[index] = { ...newServers[index], [field]: value };
    setServers(newServers);
  };

  const renderServerForm = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-slate-800 flex items-center gap-2">
          <Server className="w-5 h-5" />
          Server Settings
        </h4>
        <Button
          variant="outline"
          size="sm"
          onClick={addServer}
          className="flex items-center gap-1"
        >
          <Plus className="w-4 h-4" />
          Add Server
        </Button>
      </div>
      
      {servers.map((server, index) => (
        <div key={index} className="space-y-3 p-4 bg-gray-50 rounded-lg border">
          <div className="flex items-center justify-between">
            <h5 className="font-medium">Server {index + 1}</h5>
            <div className="flex items-center gap-2">
              <button
                onClick={() => updateServer(index, 'enabled', !server.enabled)}
                className={`p-1.5 rounded ${server.enabled ? 'bg-green-500 text-white' : 'bg-gray-300'}`}
                title={server.enabled ? 'Enabled' : 'Disabled'}
              >
                {server.enabled ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              </button>
              {servers.length > 1 && (
                <button
                  onClick={() => removeServer(index)}
                  className="p-1.5 rounded bg-red-100 text-red-600 hover:bg-red-200"
                  title="Remove server"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Server Address</Label>
              <Input
                value={server.address}
                onChange={(e) => updateServer(index, 'address', e.target.value)}
                placeholder="server.example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Port</Label>
              <Input
                type="number"
                value={server.port}
                onChange={(e) => updateServer(index, 'port', parseInt(e.target.value) || 0)}
              />
            </div>
          </div>
        </div>
      ))}
      
      <p className="text-xs text-gray-500">
        {servers.length} server(s) configured. Click "Add Server" to add more.
      </p>
    </div>
  );

  const renderUserPasswordForm = () => (
    <div className="space-y-4">
      <h4 className="font-medium text-rose-800 flex items-center gap-2">
        <Lock className="w-5 h-5" />
        User Credentials
      </h4>
      
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Admin Password</Label>
          <Input
            type="password"
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            placeholder="Enter new admin password"
          />
        </div>

        <div className="space-y-2">
          <Label>User Password</Label>
          <Input
            type="password"
            value={userPassword}
            onChange={(e) => setUserPassword(e.target.value)}
            placeholder="Enter new user password"
          />
        </div>
      </div>

      <p className="text-xs text-orange-600">
        Warning: Changing passwords will require these credentials to access the device.
      </p>
    </div>
  );

  const renderM1nLargeModelForm = () => (
    <div className="space-y-4">
      <h4 className="font-medium text-violet-800 flex items-center gap-2">
        <Zap className="w-5 h-5" />
        M1N Large Model Settings
      </h4>
      
      <div className="flex items-center justify-between p-4 bg-violet-50 rounded-lg border border-violet-200">
        <div>
          <Label className="text-base">Large Model</Label>
          <p className="text-sm text-muted-foreground">
            Enable or disable the M1N Large AI Model
          </p>
        </div>
        <button
          onClick={() => setM1nLargeModelEnabled(!m1nLargeModelEnabled)}
          className={`p-3 rounded-lg transition-colors ${
            m1nLargeModelEnabled 
              ? 'bg-violet-500 text-white' 
              : 'bg-gray-300 text-gray-600'
          }`}
        >
          {m1nLargeModelEnabled ? (
            <ToggleRight className="w-6 h-6" />
          ) : (
            <ToggleLeft className="w-6 h-6" />
          )}
        </button>
      </div>

      <p className="text-xs text-gray-500">
        {m1nLargeModelEnabled ? 'Large Model is ENABLED' : 'Large Model is DISABLED'}
      </p>
    </div>
  );

  const renderCustomJsonForm = () => (
    <div className="space-y-4">
      <h4 className="font-medium text-slate-800 flex items-center gap-2">
        <FileJson className="w-5 h-5" />
        Raw MDVR JSON Payload
      </h4>
      <div className="space-y-2">
        <Label>Configuration JSON</Label>
        <textarea
          value={customJsonText}
          onChange={(e) => {
            const value = e.target.value;
            setCustomJsonText(value);
            try {
              const parsed = JSON.parse(value);
              setCustomJsonObject(parsed);
              setCustomJsonError(null);
            } catch (err) {
              setCustomJsonError('Invalid JSON format');
            }
          }}
          className="w-full p-3 border rounded font-mono text-xs min-h-[280px] bg-white"
          placeholder='{"MDVR": {}}'
        />
        <p className="text-xs text-muted-foreground">
          Paste the exact payload you want the MDVR to receive. Must be valid JSON.
        </p>
      </div>
      {customJsonError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-600">
          {customJsonError}
        </div>
      )}
      {!customJsonError && (
        <div className="text-xs text-green-700 flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          JSON is valid
        </div>
      )}
    </div>
  );

  const config = useMemo(
    () => buildConfig(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedConfig,
      aiEvents,
      canBusRate,
      canPluginType,
      speedSource,
      soundVolume,
      privacyEnabled,
      privacyAudioSuppression,
      privacyVideoOutput,
      privacyEventSensitivity,
      privacyChannels,
      privacyTriggerMode,
      remoteWakeEnabled,
      sleepMode,
      lowPowerTimeout,
      leftSignalEnabled,
      rightSignalEnabled,
      rearSignalEnabled,
      lightOffMode,
      lightOffTime,
      triggerOption,
      mainChannelEnabled,
      mainFrameRate,
      mainQuality,
      servers,
      adminPassword,
      userPassword,
      m1nLargeModelEnabled,
      customJsonObject
    ]
  );

  // Auto-update parent when config changes
  useEffect(() => {
    if (onConfigGenerated) {
      onConfigGenerated(config);
    }
  }, [config, onConfigGenerated]);

  return (
    <div className="space-y-4">
      {/* Configuration Type Selector */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {Object.values(CONFIG_TEMPLATES).map((template) => {
          const Icon = template.icon;
          const isSelected = selectedConfig === template.id;
          return (
            <button
              key={template.id}
              onClick={() => setSelectedConfig(template.id)}
              className={`p-3 rounded-lg border-2 transition-all text-left ${
                isSelected
                  ? `border-${template.color}-500 bg-${template.color}-50`
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Icon className={`w-5 h-5 mb-1 ${isSelected ? `text-${template.color}-600` : 'text-gray-400'}`} />
              <span className={`text-xs font-medium block ${isSelected ? `text-${template.color}-700` : 'text-gray-600'}`}>
                {template.name}
              </span>
            </button>
          );
        })}
      </div>

      {/* Configuration Form */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            {CONFIG_TEMPLATES[selectedConfig] && (
              <>
                {React.createElement(CONFIG_TEMPLATES[selectedConfig].icon, { className: "w-5 h-5" })}
                {CONFIG_TEMPLATES[selectedConfig].name}
              </>
            )}
          </CardTitle>
          <CardDescription>
            {CONFIG_TEMPLATES[selectedConfig]?.description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {renderConfigForm()}
        </CardContent>
      </Card>

      {/* JSON Preview */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Generated Configuration</Label>
        <pre className="bg-gray-100 p-3 rounded border text-xs overflow-x-auto max-h-48">
          {JSON.stringify(config, null, 2)}
        </pre>
      </div>
    </div>
  );
}

export default DeviceConfigPanel;
